// Global, persistent bottom tab bar for the mobile-web build.
//
// Mounted ONCE in Router.web (outside <Routes>) so it stays mounted across tab
// navigations — the bar no longer remounts/flashes on every route change, and it
// shows on Settings too (which renders via WebMainRoutes, outside the Home shell).
// It is self-contained: it derives the active tab from the URL and owns the
// navigation handler (route nav + Receive overlay + Unshield sync-gate), mirroring
// DashboardScreen.web's onNavigate so behavior stays identical.
import React, { useCallback, useEffect } from 'react'

import { setToastBottomInset } from '@common/contexts/toastContext/bottomInsetRegistry'
import { useReceiveSheet } from '@common/contexts/receiveSheetContext'
import useNavigation from '@common/hooks/useNavigation'
import useRoute from '@common/hooks/useRoute'
import useToast from '@common/hooks/useToast'
import useWindowSize from '@common/hooks/useWindowSize'
import { ROUTES } from '@common/modules/router/constants/common'
import { useTranslation } from 'react-i18next'
import useAppMode from '@web/hooks/useAppMode'
import useRailgunContext from '@web/hooks/useRailgunContext'

import { NavItem, NavKey } from '../navConfig'
import { BREAKPOINTS, LAYOUT } from '../tokens'
import BottomTabBar from './BottomTabBar'

// Map the current path → the tab that should read as active. Only these routes
// show the bar; on any other path (unlock, onboarding, send/shield sub-flows)
// the bar is hidden so it never covers a screen's own bottom CTA.
const activeForPath = (pathname: string): NavKey | null => {
  if (pathname === ROUTES.dashboard) return 'home'
  if (pathname === ROUTES.transactions) return 'activity'
  if (pathname === ROUTES.apps) return 'apps'
  if (pathname.startsWith('settings')) return 'settings'
  return null
}

const MobileTabBar: React.FC = () => {
  const { width } = useWindowSize()
  const { path } = useRoute()
  const { navigate } = useNavigation()
  const { mode } = useAppMode()
  const { openReceive } = useReceiveSheet()
  const { addToast } = useToast()
  const { t } = useTranslation()
  const { isBalanceFetching } = useRailgunContext()

  const onNavigate = useCallback(
    (item: NavItem) => {
      // Receive opens as an overlay popup, not a route change.
      if (item.key === 'receive') {
        openReceive()
        return
      }
      // Unshield needs a synced private balance — block + notify while syncing.
      if (item.key === 'unshield' && isBalanceFetching) {
        addToast(
          t('Your private balance is still syncing. Please wait until it finishes to unshield.'),
          { type: 'info' }
        )
        return
      }
      if (item.route) navigate(item.route)
    },
    [navigate, openReceive, isBalanceFetching, addToast, t]
  )

  // Bar is a mobile-tier affordance only; the desktop/tablet shell uses the Sidebar.
  const isMobileTier = width < BREAKPOINTS.tablet
  const active = activeForPath(path?.substring(1) ?? '')
  const isVisible = isMobileTier && !!active

  // Publish the reserved height so bottom-anchored toasts clear the bar. It is
  // route-gated as well as width-gated, so this can't be a fixed constant.
  useEffect(() => {
    setToastBottomInset(isVisible ? LAYOUT.bottomTabHeight : 0)

    return () => setToastBottomInset(0)
  }, [isVisible])

  if (!isVisible) return null

  return <BottomTabBar active={active as NavKey} mode={mode} onNavigate={onNavigate} />
}

export default MobileTabBar
